import { useEffect, useRef, useState } from "react";
import { useUser } from "../App";
import { questionsOf } from "../../shared/questions";
import { CHANNEL_LABEL, SHELL_TOOLS, type ApprovalDTO, type FileDTO, type MessageDTO, type PlatformEvent, type SessionDTO } from "../../shared/types";
import { canDecideRequest } from "../lib/approval-text";
import { Link } from "../lib/router";
import type { ConversationState, RunView } from "../lib/useConversation";
import { AgentAvatar } from "./AgentAvatar";
import { FileCard, FileChip } from "./Files";
import { Markdown } from "./Markdown";
import { ModelBadge } from "./ModelBadge";
import { NoteCard } from "./NoteCard";
import { HandoverCard } from "./HandoverCard";
import { ActionCard } from "./ActionCard";
import { ProposalCard } from "./ProposalCard";
import { RequestCard } from "./RequestCard";
import { ReviewCard } from "./ReviewCard";
import { RunCost } from "./CostBreakdown";
import { Collapsible, duration, StatusBadge } from "./ui";

function summarizeInput(name: string, input: any): string {
  if (!input || typeof input !== "object") return "";
  if (SHELL_TOOLS.includes(name)) return input.command ?? "";
  if (input.file_path) return input.file_path;
  if (input.pattern) return input.pattern;
  if (input.url) return input.url;
  return JSON.stringify(input).slice(0, 120);
}

function ToolCall({ call, result }: { call: PlatformEvent; result?: PlatformEvent }) {
  const p = call.payload;
  const summary = summarizeInput(p.name, p.input);
  return (
    <Collapsible
      summary={
        <span className="flex min-w-0 items-center gap-2 text-xs text-muted">
          <span className={`font-medium ${result?.payload?.isError ? "text-danger" : "text-muted"}`}>{p.name}</span>
          <span className="truncate font-mono">{summary}</span>
          {!result && <span className="size-1.5 shrink-0 animate-pulse rounded-full bg-blue-500" />}
        </span>
      }
    >
      <pre className="scroll-thin max-h-72 overflow-auto rounded bg-fill p-2 font-mono text-xs whitespace-pre-wrap">
        {JSON.stringify(p.input, null, 2)}
        {result && `\n\n→ ${result.payload.content ?? ""}${result.payload.truncated ? "\n[truncated]" : ""}`}
      </pre>
    </Collapsible>
  );
}

function Chevron({ open }: { open: boolean }) {
  return (
    <svg viewBox="0 0 20 20" className={`size-4 shrink-0 text-faint transition-transform ${open ? "rotate-90" : ""}`} fill="currentColor" aria-hidden="true">
      <path fillRule="evenodd" d="M7.2 14.8a.75.75 0 0 1 0-1.06L10.94 10 7.2 6.26a.75.75 0 1 1 1.06-1.06l4.27 4.27a.75.75 0 0 1 0 1.06L8.26 14.8a.75.75 0 0 1-1.06 0Z" clipRule="evenodd" />
    </svg>
  );
}

/** "Asked <Agent>", and the scope it works under when there is one: beside it on a wide screen, under it on a phone. */
function AskedLabel({ name, scope }: { name: string; scope?: string | null }) {
  return (
    <span className="flex min-w-0 flex-col sm:max-w-[60%] sm:flex-row sm:items-baseline sm:gap-1">
      <span className="truncate text-sm font-medium">Asked {name}</span>
      {scope && <span className="truncate text-xs font-medium text-muted sm:text-sm">· {scope}</span>}
    </span>
  );
}

/** One delegation as an accordion: compact header, full task/activity/result when expanded. */
function DelegationCard({ event, state }: { event: PlatformEvent; state: ConversationState }) {
  const p = event.payload;
  const child = state.runs[p.childRunId];
  const status = child?.status ?? "queued";
  const active = status === "queued" || status === "running";
  const [open, setOpen] = useState(active);
  const userToggled = useRef(false);

  // Follow the run: open while working, collapse when done — unless the user chose otherwise.
  useEffect(() => {
    if (!userToggled.current) setOpen(active);
  }, [active]);

  const toggle = () => {
    userToggled.current = true;
    setOpen((o) => !o);
  };

  return (
    <div className="overflow-hidden card">
      <button type="button" onClick={toggle} aria-expanded={open} className="flex w-full items-center gap-2 px-3 py-2.5 text-left hover:bg-fill-subtle">
        <Chevron open={open} />
        <AgentAvatar agent={p.agent} name={p.agentName ?? p.agent} size="sm" />
        <AskedLabel name={p.agentName ?? p.agent} scope={p.scopeName ?? child?.subject?.name} />
        {p.model && p.model !== p.defaultModel && <ModelBadge model={p.model} defaultModel={p.defaultModel} short />}
        <span className="hidden shrink-0 rounded bg-fill px-1.5 py-0.5 text-tiny tracking-wide text-muted uppercase sm:inline">{p.mode}</span>
        <StatusBadge status={status} />
        <span className="min-w-0 flex-1 truncate text-xs text-muted">{p.task}</span>
        {child && <span className="shrink-0 text-xs text-faint">{duration(child.startedAt ?? null, child.finishedAt ?? null)}</span>}
      </button>

      {open && (
        <div className="space-y-3 border-t border-line px-3 py-3">
          <div>
            <div className="mb-1 text-tiny font-medium tracking-wide text-faint uppercase">Task</div>
            <p className="text-xs whitespace-pre-wrap text-muted">{p.task}</p>
          </div>
          <Collapsible summary={<span className="text-xs text-muted">Activity</span>} defaultOpen={active}>
            <RunActivity runId={p.childRunId} state={state} />
          </Collapsible>
          {child?.result && (
            <div>
              <div className="mb-1 text-tiny font-medium tracking-wide text-faint uppercase">Result</div>
              <Markdown>{child.result}</Markdown>
            </div>
          )}
          {child && (child.status === "failed" || child.status === "cancelled") && (
            <p className="text-xs text-danger">{JSON.stringify(child.error)}</p>
          )}
          <div className="flex justify-end">
            <Link to={`/admin/runs/${p.childRunId}`} className="text-xs text-faint hover:text-muted">
              run details →
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * A delegation from the record: the ask and the answer as they were saved, for a conversation whose live events
 * are gone (history, and the pairs migration 047 backfilled). Same line as the live card, so the chat reads the same.
 */
function AskedCard({ pair }: { pair: AskedPair }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="overflow-hidden card">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 px-3 py-2.5 text-left hover:bg-fill-subtle"
      >
        <Chevron open={open} />
        <AgentAvatar agent={pair.agentSlug} name={pair.agentName} size="sm" />
        <AskedLabel name={pair.agentName} scope={pair.scopeName} />
        {pair.model && pair.model !== pair.defaultModel && <ModelBadge model={pair.model} defaultModel={pair.defaultModel} short />}
        <span className="min-w-0 flex-1 truncate text-xs text-muted">{pair.ask?.content ?? ""}</span>
      </button>
      {open && (
        <div className="space-y-3 border-t border-line px-3 py-3">
          {pair.ask && (
            <div>
              <div className="mb-1 text-tiny font-medium tracking-wide text-faint uppercase">Task</div>
              <p className="text-xs whitespace-pre-wrap text-muted">{pair.ask.content}</p>
            </div>
          )}
          {pair.answer ? (
            <div>
              <div className="mb-1 text-tiny font-medium tracking-wide text-faint uppercase">Result</div>
              <Markdown>{pair.answer.content}</Markdown>
            </div>
          ) : (
            <p className="text-xs text-faint">No answer was recorded.</p>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * A delegated run's one question to the agent that gave it the task, and the answer, as one quiet internal line on
 * each side: "Asked Pandito: …" on the specialist's run, "Geographer asked: …" on the parent's.
 */
export function questionLine(e: PlatformEvent, runId: string): string | null {
  const p = e.payload ?? {};
  if (e.type === "question.asked") {
    const items: { question: string }[] = p.questions ?? [];
    const first = `${items[0]?.question ?? ""}${items.length > 1 ? ` (+${items.length - 1} more)` : ""}`;
    return p.child === runId ? `Asked ${p.to?.name ?? "the agent that gave the task"}: ${first}` : `${p.from?.name ?? "A specialist"} asked: ${first}`;
  }
  if (e.type === "question.answered") return `${p.by?.name ?? "It"} answered: ${p.answer ?? ""}`;
  return null;
}

export function RunActivity({ runId, state }: { runId: string; state: ConversationState }) {
  const events = state.events[runId] ?? [];
  const run = state.runs[runId];
  const results = new Map<string, PlatformEvent>();
  for (const e of events) if (e.type === "tool.result" && e.payload?.toolUseId) results.set(e.payload.toolUseId, e);

  const items = events.filter((e) => ["tool.call", "delegation.created", "proxy.call", "assistant.message", "file.created", "question.asked", "question.answered"].includes(e.type));
  const streaming = state.streaming[runId];
  const finalText = run?.result;

  return (
    <div className="space-y-1.5">
      {items.map((e) => {
        if (e.type === "tool.call") return <ToolCall key={e.seq} call={e} result={results.get(e.payload.id)} />;
        if (e.type === "delegation.created") return <DelegationCard key={e.seq} event={e} state={state} />;
        if (e.type === "proxy.call")
          return (
            <div key={e.seq} className="flex items-center gap-2 pl-4 font-mono text-xs text-muted">
              <span className="text-violet-600 dark:text-violet-400">{e.payload.connector}</span>
              <span className="truncate">{e.payload.method} {e.payload.path}</span>
              <span className={e.payload.status >= 400 ? "text-danger" : "text-success"}>{e.payload.status}</span>
              <span>{e.payload.durationMs}ms</span>
            </div>
          );
        if (e.type === "question.asked" || e.type === "question.answered")
          return (
            <p key={e.seq} className="pl-4 text-xs text-muted">
              {questionLine(e, runId)}
            </p>
          );
        if (e.type === "file.created")
          return (
            <div key={e.seq} className="max-w-md">
              <FileCard file={e.payload} />
            </div>
          );
        if (e.type === "assistant.message" && !e.payload.parentToolUseId && e.payload.text !== finalText)
          return (
            <div key={e.seq} className="pl-4 text-muted [&_.md]:text-xs">
              <Markdown>{e.payload.text}</Markdown>
            </div>
          );
        return null;
      })}
      {streaming && (
        <div className="pl-4 text-muted">
          <Markdown>{`${streaming}▍`}</Markdown>
        </div>
      )}
      {run && (run.status === "queued" || run.status === "running") && !streaming && items.length === 0 && (
        <p className="pl-4 text-xs text-faint">{run.status === "queued" ? "Queued…" : "Thinking…"}</p>
      )}
    </div>
  );
}

function MessageFiles({ files, align }: { files: FileDTO[]; align: "start" | "end" }) {
  const images = files.filter((f) => f.mime.startsWith("image/") && f.mime !== "image/svg+xml");
  const others = files.filter((f) => !images.includes(f));
  return (
    <div className={`flex w-full flex-col gap-1.5 ${align === "end" ? "items-end" : "items-start"}`}>
      {images.map((f) => (
        <div key={f.id} className="w-full max-w-xs">
          <FileCard file={f} />
        </div>
      ))}
      {others.length > 0 && (
        <div className={`flex flex-wrap gap-1.5 ${align === "end" ? "justify-end" : ""}`}>
          {others.map((f) => (
            <FileChip key={f.id} file={f} />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * A quiet line where one session ended and the next picked up. Nothing was lost: the conversation is kept for
 * good, and the new session was handed the note the old one left, which opens here. A session that picked up
 * from nothing is not a roll but the start of one of the chats this conversation was folded out of (056), so it
 * says so instead of claiming a handover that never happened.
 */
function SessionDivider({ summary, earlier }: { summary: string | null; earlier: boolean }) {
  const [open, setOpen] = useState(false);
  const picked = Boolean(summary);
  return (
    <div className="py-1">
      <button
        type="button"
        onClick={() => picked && setOpen((o) => !o)}
        aria-expanded={picked ? open : undefined}
        disabled={!picked}
        className={`flex w-full items-center gap-3 text-tiny text-faint ${picked ? "hover:text-muted" : "cursor-default"}`}
      >
        <span className="h-px flex-1 bg-fill-strong" />
        <span className="shrink-0">{earlier ? "An earlier chat, from here" : `New session${picked ? " · picked up from a summary" : ""}`}</span>
        {picked && <Chevron open={open} />}
        <span className="h-px flex-1 bg-fill-strong" />
      </button>
      {open && summary && (
        <div className="mt-2 rounded-lg border border-line bg-fill-subtle px-3 py-2 text-muted [&_.md]:text-xs">
          <div className="mb-1 text-tiny font-medium tracking-wide text-faint uppercase">Where we left off</div>
          <Markdown>{summary}</Markdown>
        </div>
      )}
    </div>
  );
}

/**
 * Where the chat draws a divider, by the run that opens each session after the conversation's first: the run, not
 * a timestamp, so it can never land on the wrong side of the message that caused the roll. What the divider opens
 * is the note the session picked up **from** — its own is written later, and is for the session after it.
 */
export function sessionDividers(sessions: SessionDTO[]): Map<string, { id: string; summary: string | null; earlier: boolean }> {
  const dividers = new Map<string, { id: string; summary: string | null; earlier: boolean }>();
  for (const s of sessions.slice(1)) {
    if (!s.firstRunId) continue;
    const from = sessions.find((p) => p.id === s.seededFromSessionId);
    dividers.set(s.firstRunId, { id: s.id, summary: from?.summary ?? null, earlier: !s.seededFromSessionId });
  }
  return dividers;
}

/**
 * The one card whose question this person's next typed message would answer, by its card message's id — the same
 * rule the server claims by: a free-text question asked on its own, on the web door, waiting for them, with nothing
 * of theirs said since; the oldest when two are waiting. Every other open question keeps a box of its own on its
 * card, so a card never promises something the next message will not do.
 */
export function nextMessageAnswers(messages: MessageDTO[], approvals: Record<string, ApprovalDTO>, userId: string): string | null {
  const said = messages.findLastIndex((m) => m.role === "user" && m.userId === userId);
  const card = messages.slice(said + 1).find((m) => {
    const a = m.approvalId ? approvals[m.approvalId] : undefined;
    const questions = a ? questionsOf(a) : [];
    return (
      m.role === "assistant" && m.channel === "web" && a?.kind === "question" && a.status === "pending" &&
      a.ownerUserId === userId && questions.length === 1 && !questions[0]?.options?.length
    );
  });
  return card?.id ?? null;
}

export type AskedPair = {
  childRunId: string; agentName: string; agentSlug: string | null; ask: MessageDTO | null; answer: MessageDTO | null;
  /** The scope the delegated run worked under, by name; null for none. */
  scopeName?: string | null;
  /** The model the delegated run used and its agent's default, to say when the task was given another. */
  model?: string | null; defaultModel?: string | null;
};

/**
 * Agent-to-agent messages never show as bubbles. They group into one "Asked <Agent>" line per delegated run, placed
 * where the ask was said. A run the transcript already draws a live delegation card for is left to that card, so a
 * delegation shows exactly once.
 */
export function askedPairs(messages: MessageDTO[], runs: Record<string, RunView>, drawnLive: Set<string>): Map<string, AskedPair> {
  const pairs = new Map<string, AskedPair>();
  for (const m of messages) {
    const childRunId = m.internal ? (m.channelRef ?? m.runId) : null;
    if (!childRunId || drawnLive.has(childRunId)) continue;
    const pair = pairs.get(childRunId) ?? {
      childRunId, agentName: runs[childRunId]?.agentName ?? runs[childRunId]?.agentSlug ?? "an agent",
      agentSlug: runs[childRunId]?.agentSlug ?? null, ask: null, answer: null, scopeName: runs[childRunId]?.subject?.name ?? null,
      model: runs[childRunId]?.model ?? null, defaultModel: runs[childRunId]?.defaultModel ?? null,
    };
    // The ask comes first; anything after it in the same pair is the answer.
    if (!pair.ask) pair.ask = m;
    else pair.answer ??= m;
    pairs.set(childRunId, pair);
  }
  return pairs;
}

/**
 * Which door this was said through, when it was not this screen. One conversation holds every channel a
 * person uses, so the web chat is where they see all of it, each message marked with where it came from.
 */
function ChannelMark({ m }: { m: MessageDTO }) {
  if (!m.channel || m.channel === "web") return null;
  return <span className="text-tiny text-faint">{CHANNEL_LABEL[m.channel]}</span>;
}

function MessageBubble({ m }: { m: MessageDTO }) {
  if (m.role === "user")
    return (
      <div className="flex flex-col items-end gap-1.5">
        <div className="max-w-[88%] rounded-2xl rounded-br-sm bg-ink px-4 py-2 text-paper md:max-w-[80%] dark:bg-white dark:text-ink">
          <Markdown className="md-invert">{m.content}</Markdown>
        </div>
        <ChannelMark m={m} />
        {m.files && m.files.length > 0 && <MessageFiles files={m.files} align="end" />}
      </div>
    );
  if (m.role === "system")
    return (
      <div className="rounded-lg border border-dashed border-line px-3 py-2 text-muted [&_.md]:text-xs">
        <Markdown>{m.content}</Markdown>
      </div>
    );
  return (
    <div className="space-y-1.5">
      <Markdown>{m.content}</Markdown>
      <ChannelMark m={m} />
      {m.files && m.files.length > 0 && <MessageFiles files={m.files} align="start" />}
    </div>
  );
}

function RunFooter({ run }: { run: RunView }) {
  if (run.status === "queued" || run.status === "running") return null;
  return (
    <div className="flex items-center gap-2 text-tiny text-faint">
      <StatusBadge status={run.status} />
      <span>{duration(run.startedAt ?? null, run.finishedAt ?? null)}</span>
      <RunCost usd={run.costUsd} paidBy={run.paidBy} />
      <Link to={`/admin/runs/${run.id}`} className="hover:text-muted">
        run details
      </Link>
    </div>
  );
}

/**
 * Where a Slack message's card is drawn: the earliest version in the transcript holds the newest version of it, so a
 * version sent back is replaced where it stood; a later version's own place draws nothing (null). Any other card, and
 * a version whose earlier one is not in the loaded transcript, is drawn where it is.
 */
function inPlace(a: ApprovalDTO, approvals: Record<string, ApprovalDTO>, drawn: Set<string>): ApprovalDTO | null {
  if (a.template !== "slack-message") return a;
  if (a.revisionOf && approvals[a.revisionOf] && drawn.has(a.revisionOf)) return null;
  let newest = a;
  for (let seen = 0; seen < 50; seen++) {
    const next = Object.values(approvals).find((x) => x.revisionOf === newest.id);
    if (!next) break;
    newest = next;
  }
  return newest;
}

export function Transcript({ state }: { state: ConversationState }) {
  const user = useUser();
  const bottom = useRef<HTMLDivElement>(null);
  const eventCount = Object.values(state.events).reduce((n, l) => n + l.length, 0);
  const streamLen = Object.values(state.streaming).reduce((n, s) => n + s.length, 0);
  useEffect(() => {
    bottom.current?.scrollIntoView({ block: "end" });
  }, [state.messages.length, eventCount, streamLen]);

  const shownActivity = new Set<string>();
  const blocks: React.ReactNode[] = [];
  // Delegations the live events already draw a card for; everything else internal becomes an "Asked <Agent>" line.
  const drawnLive = new Set<string>();
  for (const list of Object.values(state.events)) for (const e of list) if (e.type === "delegation.created" && e.payload?.childRunId) drawnLive.add(e.payload.childRunId);
  const pairs = askedPairs(state.messages, state.runs, drawnLive);
  const shownPairs = new Set<string>();
  const dividers = sessionDividers(state.sessions);
  // The one question their next message answers; every other card types its answer in place.
  const nextAnswers = nextMessageAnswers(state.messages, state.approvals, user.id);
  const cards = new Set(state.messages.filter((m) => m.approvalId && m.role === "assistant").map((m) => m.approvalId!));
  const shownDividers = new Set<string>();
  for (const m of state.messages) {
    const divider = m.runId ? dividers.get(m.runId) : undefined;
    if (divider && !shownDividers.has(divider.id)) {
      shownDividers.add(divider.id);
      blocks.push(<SessionDivider key={`session-${divider.id}`} summary={divider.summary} earlier={divider.earlier} />);
    }
    if (m.internal) {
      const childRunId = m.channelRef ?? m.runId;
      const pair = childRunId ? pairs.get(childRunId) : undefined;
      if (pair && !shownPairs.has(pair.childRunId)) {
        shownPairs.add(pair.childRunId);
        blocks.push(<AskedCard key={`asked-${pair.childRunId}`} pair={pair} />);
      }
      continue;
    }
    const run = m.runId ? state.runs[m.runId] : undefined;
    // Root runs (chat turns and task runs) show their live activity under the message that started them.
    const isTrigger = run && !run.parentRunId && (m.role === "user" || m.role === "system") && !shownActivity.has(run.id);
    const filed = m.approvalId ? state.approvals[m.approvalId] : undefined;
    // A Slack message sent back for changes is replaced in place: its newest version is drawn where the first was.
    const placed = filed && m.role === "assistant" ? inPlace(filed, state.approvals, cards) : filed;
    if (filed && placed === null) continue;
    const approval = placed ?? undefined;
    // The agent's request is a card; the platform's note back to it is not drawn at all (the card shows the outcome).
    const card = approval && m.role === "assistant";
    if (card)
      blocks.push(
        approval.kind === "question" ? (
          // One rule for every card (D14): only the person a request is addressed to decides it, or — addressed to the
          // agent's owners — any one of them. An admin reading somebody else's chat reads the card and leaves it alone.
          // Only the question their next message would answer says so; every other free-text one gets a field here,
          // whether it is in a set, asked on another door, or left behind by something they have said since.
          <RequestCard key={m.id} approval={approval} canAnswer={canDecideRequest(approval, user)} canTypeHere={m.id !== nextAnswers} />
        ) : approval.kind === "note" ? (
          <NoteCard key={m.id} approval={approval} canDecide={canDecideRequest(approval, user)} />
        ) : approval.kind === "review" ? (
          <ReviewCard key={m.id} approval={approval} canDecide={canDecideRequest(approval, user)} linkToPage />
        ) : approval.kind === "schedule_handover" ? (
          <HandoverCard key={m.id} approval={approval} canDecide={canDecideRequest(approval, user)} />
        ) : approval.kind === "action" ? (
          <ActionCard key={m.id} approval={approval} canDecide={canDecideRequest(approval, user)} />
        ) : (
          <ProposalCard key={m.id} approval={approval} canDecide={canDecideRequest(approval, user)} admin={user.role === "admin"} />
        ),
      );
    else if (!(m.approvalId && m.role === "system")) blocks.push(<MessageBubble key={m.id} m={m} />);
    if (isTrigger) {
      shownActivity.add(run.id);
      blocks.push(
        <div key={`act-${run.id}`} className="border-l-2 border-line pl-2">
          <RunActivity runId={run.id} state={state} />
        </div>,
      );
    } else if (run && m.role !== "user" && !card && shownActivity.has(run.id)) {
      blocks.push(<RunFooter key={`footer-${m.id}`} run={run} />);
    } else if (run && m.role === "user" && shownActivity.has(run.id) && (run.status === "queued" || run.status === "running")) {
      // Said while the turn works (live input): it reaches that turn at its next step, and the one reply covers it.
      blocks.push(<p key={`added-${m.id}`} className="pl-4 text-xs text-faint">Added to what {run.agentName} is working on</p>);
    }
  }

  return (
    <div className="mx-auto max-w-3xl space-y-4 px-3 py-4 md:px-4 md:py-6">
      {blocks}
      <div ref={bottom} />
    </div>
  );
}
